import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    getPools, getPoolStats, clearPoolData, deletePool,
    getParticipants, deletePoolParticipants,
    getPoolEntries,
    getTournaments, deleteTournament, resetTournamentGames,
    getIncompletePicks, getRegistrations, getRounds
} from '../services/api';
import AdminDashboard     from './AdminDashboard';
import AdminGameResults   from './AdminGameResults';
import AdminParticipants     from './AdminParticipants';
import AdminCommunications  from './AdminCommunications';
import AdminSeasonSetupShell from './AdminSeasonSetupShell';
import AdminPickEntry     from './AdminPickEntry';
import AdminAppSettings   from './AdminAppSettings';
import AdminBilling       from './AdminBilling';
import ConfirmDialog      from '../components/ConfirmDialog';



// Tabs that show the pool dropdown in the header.
// Pool Config is intentionally excluded — it has its own pool selector inside the tab.
const POOL_TABS = new Set(['dashboard', 'appsettings']);

// Tabs that should also hide the tournament dropdown — these tabs manage their
// own selection (Pool Config has its own tournament selector). Participants
// also gets its own independent Organizer/Tournament/Pool filters now, and
// the global dropdown here was purely confusing: it didn't filter the
// participant list directly, but silently changed which pool the (now-fixed)
// Pool filter checked against, via the auto-selected activePool.
const NO_TOURNAMENT_DROPDOWN = new Set(['billing', 'participants']);

function Admin({ user = {}, onUserChange, onBrandingUpdated }) {
    const [tournaments,      setTournaments]      = useState([]);
    const [activeTournament, setActiveTournament] = useState(null);
    const [pools,            setPools]            = useState([]);
    const [activePool,       setActivePool]       = useState(null);
    const [stats,            setStats]            = useState(null);
    const [participants,     setParticipants]     = useState([]);
    const [entries,          setEntries]          = useState([]);
    const [incompletePicks,  setIncompletePicks]  = useState([]);
    const [pendingRegCount,  setPendingRegCount]  = useState(0);
    const [rounds,           setRounds]           = useState([]);
    const [activeTab,        setActiveTab]        = useState('dashboard');
    const [dialog,           setDialog]           = useState(null);
    const [feedback,         setFeedback]         = useState('');
    const [feedbackType,     setFeedbackType]     = useState('success');
    const feedbackTimeoutRef = useRef(null);

    // Errors persist until manually dismissed (×) or replaced by a later
    // message -- auto-dismissing an error risked it vanishing before the
    // user had a chance to actually read it, especially on a long admin
    // page. Success messages still auto-clear, since there's nothing to
    // read twice and leaving them up just adds clutter.
    const showFeedback = useCallback((msg, type = 'success') => {
        if (feedbackTimeoutRef.current) {
            clearTimeout(feedbackTimeoutRef.current);
            feedbackTimeoutRef.current = null;
        }
        setFeedback(msg);
        setFeedbackType(type);
        if (type === 'success') {
            feedbackTimeoutRef.current = setTimeout(() => setFeedback(''), 3000);
        }
    }, []);

    // Shared by any rename/update path (GeneralSettings' rename field,
    // AdminSeasonSetup's edit form): the top-level TOURNAMENT dropdown maps
    // over `tournaments`, a separate array from `activeTournament` -- a
    // rename that only updated activeTournament left the dropdown showing
    // the stale name until a full page refresh re-fetched the list.
    const handleTournamentUpdated = (updated) => {
        setActiveTournament(updated);
        setTournaments(prev => prev.map(t => t.id === updated.id ? updated : t));
    };

    const loadParticipants = () => {
        // GET /participants is now organizer-scoped: platform admins get
        // the full global list, regular organizers get their own pools'
        // participants only (plain participants -- no organizer role at
        // all -- get 403, but the Manage tab isn't shown to them in the
        // first place, so that path shouldn't normally be reachable here).
        getParticipants()
            .then(res => setParticipants(res.data))
            .catch(err => {
                showFeedback(err.response?.data?.error || 'Failed to load participants', 'error');
            });
    };

    const loadStats = () => {
        if (!activePool) return;
        getPoolStats(activePool.id).then(res => setStats(res.data));
    };

    const loadEntries = () => {
        if (!activePool) return;
        getPoolEntries(activePool.id).then(res => setEntries(res.data));
    };

    const loadDashboardData = (tournamentId) => {
        getIncompletePicks(tournamentId)
            .then(res => setIncompletePicks(res.data))
            .catch(() => setIncompletePicks([]));
        getRounds(tournamentId)
            .then(res => setRounds(res.data))
            .catch(() => setRounds([]));
        getRegistrations('pending')
            .then(res => setPendingRegCount(res.data.length))
            .catch(() => setPendingRegCount(0));
    };

    useEffect(() => {
        getTournaments().then(res => {
            setTournaments(res.data);
            if (res.data.length > 0) setActiveTournament(res.data[0]);
        });
        loadParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!activeTournament) return;
        loadDashboardData(activeTournament.id);
        getPools(activeTournament.id).then(res => {
            setPools(res.data);
            if (res.data.length > 0) {
                setActivePool(res.data[0]);
            } else {
                setActivePool(null);
                setStats(null);
                setEntries([]);
            }
        });
    }, [activeTournament]);

    useEffect(() => {
        if (!activePool) {
            setStats(null);
            setEntries([]);
            return;
        }
        getPoolStats(activePool.id).then(res => setStats(res.data));
        getPoolEntries(activePool.id).then(res => setEntries(res.data));
    }, [activePool]);

    const handleClearPool = () => {
        setDialog({
            message: `This will remove all picks, results, and entries from "${activePool?.name}". Pool settings and scoring rules are kept.`,
            requireTyped: true,
            onConfirm: async () => {
                try {
                    await clearPoolData(activePool.id);
                    showFeedback('Pool data cleared successfully');
                    loadStats(); loadEntries();
                } catch (err) {
                    showFeedback('Failed to clear pool data', 'error');
                }
                setDialog(null);
            }
        });
    };

    const handleDeletePool = () => {
        setDialog({
            message: `This will permanently delete "${activePool?.name}" and ALL of its data. This cannot be undone.`,
            requireTyped: true,
            onConfirm: async () => {
                try {
                    await deletePool(activePool.id);
                    showFeedback('Pool deleted');
                    const res = await getPools(activeTournament.id);
                    setPools(res.data);
                    setActivePool(res.data.length > 0 ? res.data[0] : null);
                } catch (err) {
                    showFeedback('Failed to delete pool', 'error');
                }
                setDialog(null);
            }
        });
    };

    const handleDeletePoolParticipants = () => {
        setDialog({
            message: `Remove ALL participants from "${activePool?.name}"? This deletes all their entries and picks for this pool.`,
            requireTyped: true,
            onConfirm: async () => {
                try {
                    await deletePoolParticipants(activePool.id);
                    showFeedback('All participants removed from pool');
                    loadStats();
                    loadEntries();
                } catch (err) {
                    showFeedback('Failed to remove participants', 'error');
                }
                setDialog(null);
            }
        });
    };

    const handleDeleteTournament = () => {
        setDialog({
            message: `This will permanently delete "${activeTournament?.name}" and ALL associated data — pools, picks, games, rounds, seeds, and scoring rules. This cannot be undone.`,
            requireTyped: true,
            onConfirm: async () => {
                try {
                    await deleteTournament(activeTournament.id);
                    showFeedback('Tournament deleted successfully');
                    const res = await getTournaments();
                    setTournaments(res.data);
                    if (res.data.length > 0) {
                        setActiveTournament(res.data[0]);
                    } else {
                        setActiveTournament(null);
                        setPools([]);
                        setActivePool(null);
                        setStats(null);
                        setEntries([]);
                    }
                } catch (err) {
                    showFeedback(err.response?.data?.error || 'Failed to delete tournament', 'error');
                }
                setDialog(null);
            }
        });
    };

    const handleResetGames = () => {
        setDialog({
            message: `This will delete all games for "${activeTournament?.name}" and re-run Initialize. Any picks must be removed first — the action will refuse if picks exist.`,
            requireTyped: true,
            onConfirm: async () => {
                try {
                    const res = await resetTournamentGames(activeTournament.id);
                    showFeedback(res.data.message || 'Games reset successfully');
                } catch (err) {
                    showFeedback(err.response?.data?.error || 'Failed to reset games', 'error');
                }
                setDialog(null);
            }
        });
    };

    const tabs = [
        { id: 'dashboard',    label: 'Dashboard' },
        { id: 'season',       label: 'Season Setup' },
        { id: 'results',      label: 'Game Results' },
        { id: 'pickentry',    label: 'Pick Entry' },
        { id: 'participants',    label: 'Participants' },
        { id: 'communications', label: 'Communications' },
        { id: 'appsettings',    label: 'App Settings' },
        // Only shown for eval-plan organizers -- see AdminBilling.js for
        // why this is a separate entry point from BecomeOrganizer.js's
        // pre-payment picker.
        ...(user.plan_id === 'eval' ? [{ id: 'billing', label: 'Upgrade Plan' }] : []),
    ];

    const showPoolDropdown       = POOL_TABS.has(activeTab);
    const showTournamentDropdown = !NO_TOURNAMENT_DROPDOWN.has(activeTab);

    return (
        <div className="max-w-5xl mx-auto px-4 py-8">
            {dialog && (
                <ConfirmDialog
                    message={dialog.message}
                    requireTyped={dialog.requireTyped}
                    onConfirm={dialog.onConfirm}
                    onCancel={() => setDialog(null)}
                />
            )}

            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h2 className="text-2xl font-black text-white uppercase tracking-wide">Admin Panel</h2>
                <div className="flex items-center gap-4 flex-wrap">
                    {showTournamentDropdown && (
                        <div className="flex flex-col gap-1">
                            <span className="text-xs text-gray-500 uppercase tracking-wide">Tournament</span>
                            <select
                                value={activeTournament?.id || ''}
                                onChange={e => setActiveTournament(tournaments.find(t => t.id === parseInt(e.target.value)))}
                                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                                {tournaments.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}{t.is_template ? ' (Template)' : ''}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {showPoolDropdown && (
                        <div className="flex flex-col gap-1">
                            <span className="text-xs text-gray-500 uppercase tracking-wide">Pool</span>
                            <select
                                value={activePool?.id || ''}
                                onChange={e => setActivePool(pools.find(p => p.id === parseInt(e.target.value)))}
                                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                                {pools.length === 0 && <option value="">No pools</option>}
                                {pools.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {feedback && (
                <div className={'fixed top-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-xl px-4 py-3 rounded-lg text-sm font-medium flex items-center justify-between shadow-lg ' +
                    (feedbackType === 'success'
                        ? 'bg-green-900/95 border border-green-600 text-green-300'
                        : 'bg-red-900/95 border border-red-500 text-red-300')}>
                    <span>{feedback}</span>
                    <button onClick={() => setFeedback('')} className="ml-4 text-lg font-black opacity-60 hover:opacity-100">×</button>
                </div>
            )}

            <div className="flex gap-1 mb-8 border-b border-gray-800 overflow-x-auto">
                {tabs.map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        className={'px-2 py-2 text-xs font-bold rounded-t-lg transition-colors ' +
                            (activeTab === tab.id
                                ? 'bg-gray-950 text-orange-400 border-t border-x border-gray-800'
                                : 'text-gray-500 hover:text-gray-300')}>
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'dashboard' && (
                <AdminDashboard
                    stats={stats}
                    incompletePicks={incompletePicks}
                    pendingRegCount={pendingRegCount}
                    rounds={rounds}
                    activeTournament={activeTournament}
                    onNavigateToRegistrations={() => setActiveTab('participants')}
                />
            )}
            {activeTab === 'results' && (
                <AdminGameResults
                    showFeedback={showFeedback}
                    activeTournament={activeTournament}
                />
            )}
            {activeTab === 'participants' && (
                <AdminParticipants
                    participants={participants}
                    pools={pools}
                    entries={entries}
                    onRefresh={loadParticipants}
                    showFeedback={showFeedback}
                    currentUserId={user?.id}
                    isPlatformAdmin={user?.is_platform_admin}
                />
            )}
            {activeTab === 'season' && (
                <AdminSeasonSetupShell
                    user={user}
                    showFeedback={showFeedback}
                    activeTournament={activeTournament}
                    tournaments={tournaments}
                    activePool={activePool}
                    onTournamentCreated={async (newTournament) => {
                        const res = await getTournaments();
                        setTournaments(res.data);
                        setActiveTournament(newTournament);
                    }}
                    onTournamentUpdated={handleTournamentUpdated}
                    onPoolsChanged={async () => {
                        if (!activeTournament) return;
                        const res = await getPools(activeTournament.id);
                        setPools(res.data);
                        if (activePool && !res.data.find(p => p.id === activePool.id)) {
                            setActivePool(res.data.length > 0 ? res.data[0] : null);
                        }
                    }}
                />
            )}
            {activeTab === 'pickentry' && (
                <AdminPickEntry
                    showFeedback={showFeedback}
                    activeTournament={activeTournament}
                />
            )}
            {activeTab === 'communications' && (
                <AdminCommunications
                    activeTournament={activeTournament}
                    showFeedback={showFeedback}
                />
            )}

            {activeTab === 'appsettings' && (
                <AdminAppSettings
                    user={user}
                    onUserChange={onUserChange}
                    onBrandingUpdated={onBrandingUpdated}
                    showFeedback={showFeedback}
                    activePool={activePool}
                    activeTournament={activeTournament}
                    onClearPool={handleClearPool}
                    onDeletePool={handleDeletePool}
                    onDeletePoolParticipants={handleDeletePoolParticipants}
                    onDeleteTournament={handleDeleteTournament}
                    onResetGames={handleResetGames}
                    onTournamentUpdated={handleTournamentUpdated}
                />
            )}

            {activeTab === 'billing' && (
                <AdminBilling user={user} />
            )}
        </div>
    );
}

export default Admin;